import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { resolveStudentIdentity } from "@/lib/profile";
import TwoFactorSetup from "../components/TwoFactorSetup";
import LearningProfile from '@/components/feature/panel/LearningProfile';

// Kayıt sırasında kullanıcı kimliğinden türetilen eşleştirme kodu.
function deriveMatchCode(userId: string): string {
  const seed = userId.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  const part = seed.slice(0, 6) || "000000";
  return `DD-${part}`;
}

export default function Profilim() {
  const { user } = useAuth();
  const identity = resolveStudentIdentity(user);
  const [copied, setCopied] = useState(false);

  const matchCode = user?.id ? deriveMatchCode(user.id) : "DD-000000";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(matchCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const rows = [
    { label: "Ad Soyad", value: identity.name, icon: "ri-user-line" },
    { label: "Sınıf", value: identity.grade || "Belirtilmedi", icon: "ri-graduation-cap-line" },
    { label: "Şehir", value: identity.city || "Belirtilmedi", icon: "ri-map-pin-line" },
    { label: "E-posta", value: user?.email || "—", icon: "ri-mail-line" },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 tracking-tight">Profilim</h1>
        <p className="text-sm md:text-base text-foreground-600 mt-1">
          Hesap bilgilerin ve veli eşleştirme kodun.
        </p>
      </div>

      {/* Profil kartı */}
      <LearningProfile />
      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex items-center gap-4">
          <span className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-500 text-background-50 font-heading font-bold text-xl">
            {identity.initials}
          </span>
          <div>
            <p className="font-heading font-semibold text-base text-foreground-950">{identity.name}</p>
            <p className="text-sm text-foreground-500">
              {identity.grade ? `${identity.grade} Öğrencisi` : "Öğrenci"}
              {identity.city ? ` · ${identity.city}` : ""}
            </p>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {rows.map((row) => (
            <div
              key={row.label}
              className="flex items-center gap-3 rounded-md bg-background-100 px-4 py-3"
            >
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-50 text-foreground-500 shrink-0">
                <i className={row.icon} aria-hidden="true"></i>
              </span>
              <div className="min-w-0">
                <p className="text-xs text-foreground-500">{row.label}</p>
                <p className="text-sm font-medium text-foreground-900 truncate">{row.value}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Eşleştirme kodu */}
      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground-900">
          <i className="ri-link text-primary-600" aria-hidden="true"></i>
          Eşleştirme Kodu
        </p>
        <p className="mt-1 text-sm text-foreground-600 leading-relaxed">
          Bu kod, kayıt olduğunda senin için otomatik oluşturuldu. Veline bu kodu ver;
          veli hesabını seninle eşleştirmek için kullanacak.
        </p>

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <code className="flex-1 rounded-md bg-background-100 px-4 py-3 font-heading font-bold text-lg tracking-[0.3em] text-primary-700 text-center">
            {matchCode}
          </code>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center justify-center gap-2 h-11 px-4 rounded-md bg-secondary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-secondary-600 transition-colors"
          >
            <i className={copied ? "ri-check-line" : "ri-file-copy-line"} aria-hidden="true"></i>
            {copied ? "Kopyalandı" : "Kodu Kopyala"}
          </button>
        </div>
      </div>

      {/* İki faktörlü doğrulama */}
      <TwoFactorSetup />
    </div>
  );
}
